/* payScheduleDisplay.js -- HOW A PAY SCHEDULE IS SHOWN (bi-weekly payroll, step 4, 2026-10-08).
 *
 * ONE owner for the words the two screens share: the Step-5a "Pay schedule" panel (the wizard -- the only writer) and the
 * Payroll page's read-only line. It owns NO rule and NO date arithmetic: every date comes from engines/paySchedule.js
 * (payPeriod / periodIndexOf), so the preview an operator reads before saving is the calendar the engine will use.
 * A second calendar here is forbidden (Principle 0).
 *
 * Plain ASCII on purpose (CLAUDE.md 12.5 rule 55): the dash and the middle dot the screen shows are written as escapes. */
import { addDays, isBiweekly, monthEndIso, payCalendarProblem, payPeriod, periodIndexOf, weekdayOf } from '../engines/paySchedule';

export const PAY_FREQUENCY_LABELS = { semi_monthly: 'Semi-monthly', biweekly: 'Bi-weekly' };
export const PAYROLL_BOOKS_LABELS = { accrues: 'Accrues payroll', when_paid: 'Expenses payroll when paid' };
export const ACCRUAL_COVERS_LABELS = { wages: 'Wages', wages_and_taxes: 'Wages + employer taxes' };

/* Brian, 2026-10-08 (Q5), VERBATIM. Shown wherever the books answer "Expenses payroll when paid" is displayed. */
export const WHEN_PAID_STATEMENT = "Payroll is expensed when paid: each month's salary follows the checks dated in that month. Benefits and headcount follow the same months, so a new hire's first month can show no benefits, and a leaver's final check can fall in the month after their last day.";

export const UPLOAD_REQUIRED_MESSAGE = 'Required — choose how payroll is paid';
export const NO_ACCRUED_LINE_MESSAGE = 'No balance-sheet line is typed as an accrued liability — set one at Step 4 first.';
export const ACCOUNT_REQUIRED_MESSAGE = 'Required — choose the accrued-payroll account.';
export const FIRST_CHECK_HINT = 'a regular check date — not one moved for a holiday';

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// 'Fri 2026-09-11'
export const dayLabel = iso => WEEKDAY_SHORT[weekdayOf(iso)] + ' ' + iso;

/* The next `count` pay periods FROM THE CLOSE MONTH: the first is the period that contains the first day after the close
   month (the first day the forecast books); with no close month, the period of the first check date itself. Each entry is
   payPeriod's own object. A schedule that cannot make a calendar returns []. */
export function previewPayPeriods(schedule, closeMonth, count = 6) {
  if (payCalendarProblem(schedule)) return [];
  const k0 = /^\d{4}-\d{2}$/.test(String(closeMonth || '')) ? periodIndexOf(schedule, addDays(monthEndIso(closeMonth), 1)) : 0;
  const out = [];
  for (let i = 0; i < count; i++) out.push(payPeriod(schedule, k0 + i));
  return out;
}
// 'Mon 2026-08-24 - Sun 2026-09-06 . paid Fri 2026-09-11' (an en dash and a middle dot on screen); a moved check says so.
export function payPeriodLine(p) {
  return dayLabel(p.start) + ' – ' + dayLabel(p.end) + ' · paid ' + dayLabel(p.check)
    + (p.moved ? ' (moved from ' + dayLabel(p.nominalCheck) + ', ' + p.reason + ')' : '');
}

/* The stored schedule in one line. `schedule` is the engine's object (payScheduleFromSettings); a company that was never
   asked (frequency null) READS as semi-monthly (Brian A7). `accountLabel` is the linked accrued-payroll account's label, or
   null when none is linked. */
export function payScheduleSummary(schedule, accountLabel) {
  if (!isBiweekly(schedule)) return PAY_FREQUENCY_LABELS.semi_monthly;
  const lag = schedule.lagDays;
  const parts = [
    PAY_FREQUENCY_LABELS.biweekly,
    'first check ' + (schedule.firstCheckDate || 'not set'),
    lag === null || lag === undefined ? 'days to the check date not set' : 'paid ' + lag + ' day' + (lag === 1 ? '' : 's') + ' after the period ends',
    PAYROLL_BOOKS_LABELS[schedule.books] || 'books not set',
  ];
  if (schedule.books === 'accrues') {
    parts.push(ACCRUAL_COVERS_LABELS[schedule.accrualCovers] ? 'the accrual covers ' + ACCRUAL_COVERS_LABELS[schedule.accrualCovers].toLowerCase() : 'what the accrual covers is not set');
    parts.push(accountLabel ? 'account ' + accountLabel : 'no accrued-payroll account linked');
  }
  return parts.join(' · ');
}
// Whether the Q5 statement belongs beside this schedule.
export const showsWhenPaidStatement = schedule => isBiweekly(schedule) && schedule.books === 'when_paid';
